import { createFileRoute } from '@tanstack/react-router';
import { useRef } from 'react';
import { ingredientDetailQuery, useIngredient } from '@/api/queries/ingredients';
import { IngredientForm } from '@/features/ingredients/IngredientForm';
import { IngredientLoadFailed, IngredientMissing, isMissingError } from '@/features/ingredients/IngredientNotFound';

export const Route = createFileRoute('/_app/ingredients/$id/edit')({
  loader: async ({ context, params }) => {
    const id = Number(params.id);
    await context.queryClient.ensureQueryData(ingredientDetailQuery(id));
  },
  // Only a 404 (or a link that is not an id) is "missing". A 503 or a
  // dropped connection on the pass must not say the ingredient was deleted.
  errorComponent: ({ error }) => (isMissingError(error) ? <IngredientMissing /> : <IngredientLoadFailed />),
  component: EditIngredient,
});

function EditIngredient() {
  const { id } = Route.useParams();
  const { data: ingredient } = useIngredient(Number(id));
  // In Trash when the page opened: say so. Moved to Trash while open (this
  // form's own "Move to trash" is on its way out; someone else's shows up
  // as a failed save) keeps the form, so a draft is never snatched away.
  // Remembered per id: the same route instance serves the next ingredient
  // when only the id in the URL changes.
  const opened = useRef<{ id: number; inTrash: boolean } | null>(null);
  if (ingredient && opened.current?.id !== ingredient.id) {
    opened.current = { id: ingredient.id, inTrash: Boolean(ingredient.deleted_at) };
  }

  // The loader already ensured this query resolved before the route
  // rendered; this only guards the instant between an in-app navigation
  // (where the loader's promise may still be settling) and the cache
  // actually holding data.
  if (!ingredient) return null;
  if (opened.current?.inTrash) return <IngredientMissing inTrash />;

  // Keyed by id: moving from one ingredient to another starts a fresh form.
  return <IngredientForm key={ingredient.id} mode="edit" ingredient={ingredient} />;
}
